import { COMPARISON_SCENARIO_OPTIONS, OP2_LATEST_FINALIZED_YEAR } from '../constants/mfr-constants';
import { QTD_SUFFIX, YTD_SUFFIX } from './PeriodGenerator';

const QUARTER_GUIDANCE: Record<number, { value: string; label: string }> = {
  1: COMPARISON_SCENARIO_OPTIONS.Q1G,
  2: COMPARISON_SCENARIO_OPTIONS.Q2G,
  3: COMPARISON_SCENARIO_OPTIONS.Q3G,
  4: COMPARISON_SCENARIO_OPTIONS.Q4G
};

/**
 * Extracts the 4-digit year from a period string
 */
const extractYearFromPeriod = (period: string): string | null => {
  // YYYYMM, YYYYMM QTD, YYYYMM YTD
  const yyyymmMatch = period.match(/^(\d{4})\d{2}(?: (?:QTD|YTD))?$/);
  if (yyyymmMatch) return yyyymmMatch[1];
  
  // Q1-YY or FYYY
  const shortYearMatch = period.match(/^(?:Q[1-4]-|FY)(\d{2})$/);
  if (shortYearMatch) return '20' + shortYearMatch[1];
  
  return null;
};

/**
 * Extracts quarter number (1-4) from a period string by deriving from month
 */
const extractQuarterFromPeriod = (period: string): number | null => {
  // Q1-YY format
  const qMatch = period.match(/^Q([1-4])-\d{2}$/);
  if (qMatch) return parseInt(qMatch[1]);

  // YYYYMM or YYYYMM QTD - derive quarter from month (excludes YTD which is annual)
  const monthMatch = period.match(/^\d{4}(\d{2})(?: QTD)?$/);
  if (monthMatch) return Math.ceil(parseInt(monthMatch[1]) / 3);

  return null;
};

/**
 * Extracts month number (1-12) from a period string, null if not a month period
 */
const extractMonthFromPeriod = (period: string): number | null => {
  const monthMatch = period.match(/^\d{4}(\d{2})$/);
  return monthMatch ? parseInt(monthMatch[1]) : null;
};

/**
 * Checks if period is a year period (FY or YTD)
 */
const isYearPeriod = (period: string): boolean => 
  /^FY\d{2}$/.test(period) || period.endsWith(YTD_SUFFIX);

/**
 * Creates a dynamic OP2 comparison scenario option for the given year
 * @param year - The 4-digit year string
 * @returns An option object with value and label for OP2
 */
const createOP2Option = (year: string): { value: string; label: string } => {
  return {
    value: `OP2 - ${year}`,
    label: `OP2 ${year}`
  };
};

export const getComparisonScenarioOptions = (periodValue: string | string[]) => {
  let baseOptions: Array<{ value: string; label: string }> = [];

  if (!periodValue || periodValue === '') return baseOptions;

  const period = Array.isArray(periodValue) ? periodValue[0] : periodValue;
  
  // Handle case where array is empty or first element is undefined
  if (!period || period === '') return baseOptions;

  // Extract year and create OP2 option if finalized
  const year = extractYearFromPeriod(period);
  if (year && parseInt(year) <= parseInt(OP2_LATEST_FINALIZED_YEAR)) {
    baseOptions = [createOP2Option(year)];
  }

  // Monthly periods
  const month = extractMonthFromPeriod(period);
  if (month) {
    const quarter = extractQuarterFromPeriod(period)!;
    return [...baseOptions, QUARTER_GUIDANCE[quarter], COMPARISON_SCENARIO_OPTIONS.MOM, COMPARISON_SCENARIO_OPTIONS.YOY];
  }

  // QTD periods - incomplete quarter, no QOQ
  if (period.endsWith(QTD_SUFFIX)) {
    const quarter = extractQuarterFromPeriod(period)!;
    return [...baseOptions, QUARTER_GUIDANCE[quarter], COMPARISON_SCENARIO_OPTIONS.YOY];
  }

  // Complete quarterly periods (Q1-YY)
  const quarter = extractQuarterFromPeriod(period);
  if (quarter) {
    return [...baseOptions, QUARTER_GUIDANCE[quarter], COMPARISON_SCENARIO_OPTIONS.QOQ, COMPARISON_SCENARIO_OPTIONS.YOY];
  }

  // Annual periods
  if (isYearPeriod(period)) {
    return [...baseOptions, COMPARISON_SCENARIO_OPTIONS.Q4G, COMPARISON_SCENARIO_OPTIONS.YOY];
  }

  return baseOptions;
};
